<script lang="ts">
    import { onMount, tick } from 'svelte';
    import { getNewsletter, type NewsletterDetail } from '../../api/news.ts';
    import SkeletonLoaderNewsletter from '../../lib/News/SkeletonLoaderNewsletter.svelte';
    import Button from '../../lib/Button.svelte';
    import CaretRightIcon from '../../icons/CaretRightIcon.svelte';
    import * as t from "../../translations/News.ts";
    import { push } from 'svelte-spa-router';
    import { vvSettings } from '../../api/common.ts';

    export let params: { id: string };

    let newsletter: NewsletterDetail | null = null;
    let loading = true;
    let error = '';
    
    const showBlogs = vvSettings?.showBlogs ?? true;
    const pageHeader = showBlogs ? t.Page_Header() : t.News_Tab();

    async function loadNewsletter() {
        loading = true;
        error = '';
        try {
            const query = new URLSearchParams(window.location.hash.split('?')[1]);
            const isInformational = query.get('isInformational') === 'true';
            newsletter = await getNewsletter(parseInt(params.id), isInformational);
        } catch (e) {
            error = 'Failed to load newsletter.';
        } finally {
            loading = false;
        }
    }

    onMount(loadNewsletter);
    $: [params.id], loadNewsletter();

    $: if (!loading && newsletter) {
        tick().then(() => {
            document.dispatchEvent(new CustomEvent("vvContentLoaded"));
        });
    }

    const goBack = () => {
        push('/');
    };
</script>

<div class="w-full px-4 sm:px-6 lg:px-8 py-2 flex justify-center">
    <div class="w-full max-w-4xl">
        {#if loading}
            <div data-testid="loading-state">
                <SkeletonLoaderNewsletter />
            </div>
        {:else if error}
            <div class="py-16 text-center text-red-600 font-medium" data-testid="error-state">
                {error}
            </div>
        {:else if newsletter}
            <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="newsletter-detail">

                <!-- Back button and title -->
                <div class="flex items-center gap-3 mb-8" data-testid="header-row">
                    <Button
                        type="navigation"
                        class="w-[45px] h-[40px] flex items-center justify-center border border-[var(--vv-action-center-association-color,var(--vv-grey-power))] bg-white hover:bg-gray-50 p-1"
                        on:click={goBack}
                        aria-label="Back to newsletter list"
                        data-testid="back-button"
                    >
                        <CaretRightIcon
                            class="w-6 h-4"
                            style="transform: scaleX(-1);"
                            color="var(--vv-action-center-button-color)"
                        />
                    </Button>
                    <h1 class="text-2xl tracking-tight text-gray-800" data-testid="title">
                        {pageHeader}
                    </h1>
                </div>

                <!-- Newsletter title -->
                <h2 class="text-4xl text-gray-900 mb-8" data-testid="newsletter-title">
                    {newsletter.newsletterName}
                </h2>

                <!-- Date -->
                <div class="text-sm text-gray-600 mb-4" data-testid="date">
                    {newsletter.getDisplayedDate()}
                </div>

                <!-- Newsletter content -->
                <div class="ck-content prose max-w-none" data-testid="content">
                    {@html newsletter.content}
                </div>
            </div>
        {/if}
    </div>
</div>
